import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield, BarChart2, FileText, Sparkles, Target, Briefcase, TrendingUp, Users, Award, BookOpen, MessageCircle, LineChart, Zap } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';

const LandingPage: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="gradient-bg text-white py-20 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="mb-12">
                <img
                  src="/future lens pro logo.png"
                  alt="FutureLens Pro"
                  className="h-32 md:h-40 lg:h-48 w-auto"
                />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Your Personal Board of Career Advisors, Powered by AI
              </h1>
              <p className="text-lg md:text-xl opacity-90 max-w-2xl">
                Access strategic guidance equal to a $50,000 consulting engagement. FutureLens Pro assembles a council of AI-powered career advisors – each specializing in transformation, analysis, and strategy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-white text-primary hover:bg-white/90 w-full sm:w-auto">
                    Start Your Career Transformation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="secondary" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    See Subscription Plans
                  </Button>
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                alt="Advisory council meeting - diverse professionals collaborating"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expert Council Value Proposition */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Meet Your Advisory Council</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your career assessed by a council of specialized consultants, digitally powered. Each expert uses advanced AI and specialized prompts to provide deep insights in their domain.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Career Development Strategist */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-blue-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6 mx-auto">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Career Development Strategist</h3>
              <p className="text-gray-600 text-center mb-4">
                Maps your long-term career trajectory, identifies optimal pathways, and develops strategic positioning for maximum market value.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Strategic Planning
              </div>
            </div>

            {/* Compensation Analyst */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-emerald-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6 mx-auto">
                <BarChart2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Compensation Analyst</h3>
              <p className="text-gray-600 text-center mb-4">
                Analyzes salary benchmarks, compensation trends, and negotiation strategies to maximize your earning potential.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Compensation Strategy
              </div>
            </div>

            {/* Executive Interview Coach */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-amber-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6 mx-auto">
                <Users className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Executive Interview Coach</h3>
              <p className="text-gray-600 text-center mb-4">
                Prepares you for high-stakes interviews with tailored strategies, executive presence coaching, and tactical interview frameworks.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Interview Preparation
              </div>
            </div>

            {/* Industry Recruiter */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-rose-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mb-6 mx-auto">
                <Briefcase className="w-8 h-8 text-rose-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Industry Recruiter</h3>
              <p className="text-gray-600 text-center mb-4">
                Provides recruiter insights on what hiring managers seek, how to position your experience, and how to stand out in the market.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Hiring Perspective
              </div>
            </div>

            {/* Industry Researcher */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-cyan-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mb-6 mx-auto">
                <LineChart className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Industry Researcher</h3>
              <p className="text-gray-600 text-center mb-4">
                Analyzes market trends, emerging opportunities, and industry dynamics to position you in high-growth sectors.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Market Intelligence
              </div>
            </div>

            {/* Job Market Analyst */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-teal-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mb-6 mx-auto">
                <TrendingUp className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Job Market Analyst</h3>
              <p className="text-gray-600 text-center mb-4">
                Evaluates job market conditions, demand patterns, and competitive positioning to guide your job search strategy.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Job Market Analysis
              </div>
            </div>

            {/* Job Skills Analyst */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-violet-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center mb-6 mx-auto">
                <Award className="w-8 h-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Job Skills Analyst</h3>
              <p className="text-gray-600 text-center mb-4">
                Identifies transferable skills, reveals hidden capabilities, and designs personalized skill development roadmaps.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Skills Assessment
              </div>
            </div>

            {/* Professional Career Coach */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6 mx-auto">
                <MessageCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Professional Career Coach</h3>
              <p className="text-gray-600 text-center mb-4">
                Guides career pivots, bridges transitions, and provides tactical advice for successful professional development.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Career Coaching
              </div>
            </div>

            {/* Senior Career Analyst */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-slate-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-6 mx-auto">
                <FileText className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Senior Career Analyst</h3>
              <p className="text-gray-600 text-center mb-4">
                Provides comprehensive career analysis, identifies strategic opportunities, and delivers actionable insights for career advancement.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Career Analysis
              </div>
            </div>

            {/* Senior Job Match Strategist */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-orange-600 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6 mx-auto">
                <Sparkles className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Senior Job Match Strategist</h3>
              <p className="text-gray-600 text-center mb-4">
                Analyzes job opportunities against your profile, identifies optimal matches, and develops targeted application strategies.
              </p>
              <div className="text-sm text-gray-500 text-center">
                Specialty: Job Matching Strategy
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Council of Experts: Your Strategic Advantage</h3>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Unlike single-perspective AI tools, FutureLens Pro assembles a complete advisory board. Each specialized consultant analyzes your career from their unique vantage point, then synthesizes insights into a comprehensive strategic plan.
            </p>
          </div>
        </div>
      </section>

      {/* How FutureLens Works */}
      <section className="py-20">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How FutureLens Pro Works</h2>
            <p className="text-lg text-gray-600">
              A four-step process that transforms your career history into strategic intelligence
            </p>
          </div>

          <div className="space-y-16">
            {/* Step 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.pexels.com/photos/7658388/pexels-photo-7658388.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                  alt="Upload documents and assessments"
                  className="rounded-xl shadow-xl"
                />
              </div>
              <div className="space-y-6">
                <div className="inline-flex items-center px-5 py-2 bg-blue-100 rounded-full text-blue-700 font-bold">
                  Step 1
                </div>
                <h3 className="text-2xl md:text-3xl font-bold">Upload Resume & Complete Assessments</h3>
                <p className="text-lg text-gray-600">
                  Start by uploading your resume or LinkedIn profile. Then complete our proprietary BRAG assessment (highlighting achievements) and DREAM assessment (defining aspirations). This foundation allows your advisory council to understand both where you've been and where you want to go.
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <span>Secure document upload</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <span>BRAG framework: Capture your achievements</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <span>DREAM framework: Define your goals</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 space-y-6">
                <div className="inline-flex items-center px-5 py-2 bg-teal-100 rounded-full text-teal-700 font-bold">
                  Step 2
                </div>
                <h3 className="text-2xl md:text-3xl font-bold">Council Analyzes & Synthesizes</h3>
                <p className="text-lg text-gray-600">
                  Your advisory council goes to work. Each expert analyzes your career from their specialty: market positioning, skill gaps, brand strength, transition opportunities, and executive potential. Advanced AI models process your information through specialized prompts refined for each consulting discipline.
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0" />
                    <span>Multi-perspective analysis from specialized experts</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0" />
                    <span>Cross-industry opportunity mapping</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0" />
                    <span>Deep skill and experience synthesis</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                  alt="AI council analyzing career data"
                  className="rounded-xl shadow-xl"
                />
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img
                  src="https://images.pexels.com/photos/7413915/pexels-photo-7413915.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                  alt="Executive-grade PDF report"
                  className="rounded-xl shadow-xl"
                />
              </div>
              <div className="space-y-6">
                <div className="inline-flex items-center px-5 py-2 bg-amber-100 rounded-full text-amber-700 font-bold">
                  Step 3
                </div>
                <h3 className="text-2xl md:text-3xl font-bold">Receive Executive-Grade PDF Report</h3>
                <p className="text-lg text-gray-600">
                  Get your comprehensive career intelligence report with visual insights, strategic recommendations, and a personalized skill roadmap. This isn't a generic resume review – it's the equivalent of a $50,000 consulting engagement distilled into actionable guidance.
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <span>Professional PDF report with visualizations</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <span>Skills roadmap with development priorities</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <span>Market positioning and salary insights</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 space-y-6">
                <div className="inline-flex items-center px-5 py-2 bg-purple-100 rounded-full text-purple-700 font-bold">
                  Step 4
                </div>
                <h3 className="text-2xl md:text-3xl font-bold">Unlock Ongoing Guidance</h3>
                <p className="text-lg text-gray-600">
                  Your report is just the beginning. Access AI Coach Marta for personalized career coaching conversations and use the Skills Tracker to monitor your development progress. Premium tiers include monthly market refreshes and advanced features to accelerate your transformation.
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0" />
                    <span>AI Coach for ongoing career guidance</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0" />
                    <span>Skills Tracker for progress monitoring</span>
                  </li>
                  <li className="flex gap-3">
                    <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0" />
                    <span>Continuous career intelligence updates</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://images.pexels.com/photos/3760263/pexels-photo-3760263.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                  alt="AI Coach providing ongoing guidance"
                  className="rounded-xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Subscription Plan</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              All plans start with a one-time $197 setup fee that includes your comprehensive Career Intelligence Report, Report Visualizations, and 3 report runs. Then choose your ongoing subscription level.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200 hover:border-blue-500 transition-colors">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Starter</h3>
                <div className="mb-4">
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold">$197</span>
                    <span className="text-gray-600 ml-2">one-time setup</span>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-gray-400">$0</span>
                    <span className="text-gray-500 ml-2">/month ongoing</span>
                  </div>
                </div>
                <p className="text-gray-600">Essential career intelligence foundation</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Career Intelligence Report</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Report Visualizations</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>3 report runs included</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Standard support</span>
                </li>
              </ul>

              <Link to="/signup" className="block">
                <Button className="w-full" size="lg">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Growth - Most Popular */}
            <div className="bg-white rounded-xl shadow-2xl p-8 border-4 border-blue-600 relative transform lg:scale-105">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  MOST POPULAR
                </span>
              </div>

              <div className="mb-6 mt-4">
                <h3 className="text-2xl font-bold mb-2">Growth</h3>
                <div className="mb-4">
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold">$197</span>
                    <span className="text-gray-600 ml-2">one-time setup</span>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-blue-600">$29</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                </div>
                <p className="text-gray-600">Complete career development toolkit</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="font-semibold">Everything in Starter, plus:</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Skills Tracker</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>AI Coach access</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Enhanced support</span>
                </li>
              </ul>

              <Link to="/signup" className="block">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700" size="lg">
                  Start Growing
                </Button>
              </Link>
            </div>

            {/* Accelerator */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-gray-200 hover:border-purple-500 transition-colors">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2">Accelerator</h3>
                <div className="mb-4">
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl font-bold">$197</span>
                    <span className="text-gray-600 ml-2">one-time setup</span>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-2xl font-bold text-purple-600">$45</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                </div>
                <p className="text-gray-600">Premium career transformation experience</p>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="font-semibold">Everything in Growth, plus:</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Job Match AI</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Advanced job analysis</span>
                </li>
                <li className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span>Enhanced support</span>
                </li>
              </ul>

              <Link to="/signup" className="block">
                <Button variant="outline" className="w-full border-purple-600 text-purple-600 hover:bg-purple-50" size="lg">
                  Go Premium
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">All plans include a 14-day money-back guarantee</p>
            <p className="text-sm text-gray-500">Cancel subscription anytime. No hidden fees.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">The FutureLens Pro Advantage</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Council of Experts methodology delivers insights no single tool can match
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-5 mx-auto">
                <Target className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold mb-3">Cross-Industry Mapping</h3>
              <p className="text-gray-600 text-sm">
                Discover hidden opportunities across different sectors based on your transferable skills and experience patterns.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
              <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mb-5 mx-auto">
                <LineChart className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-lg font-bold mb-3">Skill Gap Analysis</h3>
              <p className="text-gray-600 text-sm">
                Identify precise competency gaps and receive prioritized learning roadmaps aligned with your career goals.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-5 mx-auto">
                <BookOpen className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold mb-3">Development Strategies</h3>
              <p className="text-gray-600 text-sm">
                Get actionable professional development plans with specific resources, timelines, and milestone tracking.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center mb-5 mx-auto">
                <Shield className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold mb-3">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Enterprise-grade security ensures your career data remains confidential and protected at all times.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Ongoing Benefits That Stick</h3>
                <p className="text-gray-700 mb-6">
                  Unlike one-time reports, FutureLens Pro provides continuous value. Track skill development, access coaching when you need it, and receive updated market insights as your career evolves.
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">AI Coach available 24/7 for career questions</span>
                  </li>
                  <li className="flex gap-3">
                    <BarChart2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Skills Tracker monitors your progress</span>
                  </li>
                  <li className="flex gap-3">
                    <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">Monthly refreshes keep insights current</span>
                  </li>
                </ul>
              </div>
              <div>
                <img
                  src="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750"
                  alt="Professional tracking progress on laptop"
                  className="rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Career Transformation Stories</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how professionals leveraged their advisory council to unlock new opportunities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=400"
                  alt="Sarah M."
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-lg">Sarah M.</h4>
                  <p className="text-sm text-gray-600">Marketing → Product Management</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "The advisory council identified transferable skills I never considered. The Market Analyst showed me where my experience was most valuable, and the Transition Coach gave me tactical advice for the pivot."
              </p>
              <div className="flex items-center text-amber-500">
                <Award className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Landed role in 8 weeks</span>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400"
                  alt="Michael C."
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-lg">Michael C.</h4>
                  <p className="text-sm text-gray-600">Finance → Data Science</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "The Skills Architect created a roadmap showing exactly what I needed to learn. Combined with the AI Coach for ongoing questions, I successfully transitioned into a data science role."
              </p>
              <div className="flex items-center text-green-600">
                <TrendingUp className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">35% salary increase</span>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400"
                  alt="Priya P."
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold text-lg">Priya P.</h4>
                  <p className="text-sm text-gray-600">Healthcare → HealthTech Leadership</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "The Brand Consultant helped me position my clinical experience as a unique advantage. The Executive Advisor mapped my leadership trajectory. Now I'm heading product strategy at a major HealthTech company."
              </p>
              <div className="flex items-center text-blue-600">
                <Sparkles className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Executive role achieved</span>
              </div>
            </div>
          </div>

          <div className="mt-16 bg-white rounded-xl p-8 shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">10K+</div>
                <p className="text-gray-600">Career Reports Generated</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-teal-600 mb-2">$850K</div>
                <p className="text-gray-600">Avg. Salary Increase</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-amber-600 mb-2">92%</div>
                <p className="text-gray-600">User Satisfaction Rate</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-purple-600 mb-2">6 Weeks</div>
                <p className="text-gray-600">Avg. Time to New Role</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="container">
          <div className="bg-gradient-to-r from-blue-600 via-teal-600 to-blue-600 rounded-2xl p-12 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <div className="relative z-10 max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Assemble Your Advisory Council?
              </h2>
              <p className="text-xl opacity-90 mb-8">
                Access strategic career guidance equal to a $50,000 consulting engagement. Start your transformation with FutureLens Pro today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/signup">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 w-full sm:w-auto">
                    Start Your Career Transformation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 w-full sm:w-auto">
                    View Subscription Plans
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm opacity-75">14-day money-back guarantee • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
